import { hash } from 'ohash'
import { useDebugLogs } from '~/composables/useDebugLogs'
import { useAuthModal } from '~/composables/useAuthModal'

export const useApi = <T>(url: string, options: any = {}) => {
    const config = useRuntimeConfig()
    // On Server, talk directly to backend (using private config) to avoid proxy loopback issues
    // On Client, use public string (proxy)
    const apiBase = import.meta.server ? `${config.apiProxyTarget}/api` : config.public.apiBase

    const token = useCookie('auth_token')

    const defaults: { baseURL: string, headers: Record<string, string> } = {
        baseURL: apiBase,
        headers: token.value ? { Authorization: `Bearer ${token.value}` } : {}
    }

    const newOptions = {
        ...defaults,
        ...options,
        headers: {
            ...defaults.headers,
            ...options.headers
        }
    }

    // Generate a unique key for hydration matching if not provided
    // This ensures Server and Client generated keys match for the same request
    if (!newOptions.key) {
        newOptions.key = hash(['api', url, options])
    }

    const { addLog } = useDebugLogs()
    const { showUnauthorizedModal } = useAuthModal()

    if (!newOptions.onResponseError) {
        newOptions.onResponseError = async (ctx: any) => {
            addLog(
                `API Error [${ctx.response.status}] ${url}`,
                'error',
                {
                    url,
                    status: ctx.response.status,
                    statusText: ctx.response.statusText,
                    data: ctx.response._data
                }
            )

            if (ctx.response.status === 401 && !url.includes('/auth/refresh-token')) {
                const refreshCookie = useCookie('refresh_token', { path: '/', maxAge: 60 * 60 * 24 * 7 })
                if (refreshCookie.value) {
                    try {
                        const response = await $fetch<any>(`${apiBase}/auth/refresh-token`, {
                            method: 'POST',
                            body: { refreshToken: refreshCookie.value }
                        })
                        useCookie('auth_token', { path: '/' }).value = response.token
                        refreshCookie.value = response.refreshToken
                        // Note: useFetch doesnt easily retry from onResponseError without hacky reload
                    } catch (e) {
                        useCookie('auth_token', { path: '/' }).value = null
                        refreshCookie.value = null
                        showUnauthorizedModal()
                    }
                } else {
                    useCookie('auth_token', { path: '/' }).value = null
                    showUnauthorizedModal()
                }
            }
        }
    }

    return useFetch<T>(url, newOptions)
}

export const $api = async <T>(url: string, options: any = {}) => {
    const config = useRuntimeConfig()
    const apiBase = import.meta.server ? `${config.apiProxyTarget}/api` : config.public.apiBase

    const getOptions = () => {
        const token = useCookie('auth_token', { path: '/' }).value
        const defaults: { baseURL: string, headers: Record<string, string> } = {
            baseURL: apiBase,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
        return {
            ...defaults,
            ...options,
            headers: { ...defaults.headers, ...options.headers }
        }
    }

    try {
        return await $fetch<T>(url, getOptions())
    } catch (err: any) {
        if (err.status === 401 && !url.includes('/auth/refresh-token')) {
            const refreshCookie = useCookie('refresh_token')
            if (refreshCookie.value) {
                try {
                    const refreshRes = await $fetch<any>(`${apiBase}/auth/refresh-token`, {
                        method: 'POST',
                        body: { refreshToken: refreshCookie.value }
                    })

                    useCookie('auth_token').value = refreshRes.token
                    refreshCookie.value = refreshRes.refreshToken

                    // Retry
                    return await $fetch<T>(url, getOptions())
                } catch (refreshErr) {
                    refreshCookie.value = null
                    useCookie('auth_token').value = null
                }
            }
        }

        // Final fallback logging
        try {
            const { addLog } = useDebugLogs()
            addLog(`API Error [${err.status}] ${url}`, 'error', { url, status: err.status, data: err.data })
            if (err.status === 401) {
                const { showUnauthorizedModal } = useAuthModal()
                showUnauthorizedModal()
            }
        } catch { }

        throw err
    }
}
